import { create } from 'zustand';
import { api } from '../utils/api-client';

export interface SubscriptionLimits {
  revenueLimitCents: number;
  aiContentLimit: number;
  affiliateLinksLimit: number;
  affiliateProgramsLimit: number;
}

export interface SubscriptionUsage {
  trackedRevenueCents: number;
  aiContentGenerated: number;
  affiliateLinksCount: number;
  affiliateProgramsCount: number;
}

export interface UsageWarnings {
  revenueApproaching: boolean;
  revenueLimitReached: boolean;
  aiApproaching: boolean;
  aiLimitReached: boolean;
}

export interface Subscription {
  id: string;
  tenantId: string;
  tier: 'starter' | 'growth' | 'pro' | 'scale';
  status: string;
  billingCycle: 'monthly' | 'annual' | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface Invoice {
  id: string;
  number: string | null;
  status: string | null;
  amountDue: number;
  amountPaid: number;
  currency: string;
  created: number;
  periodStart: number;
  periodEnd: number;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

interface SubscriptionState {
  subscription: Subscription | null;
  limits: SubscriptionLimits | null;
  usage: SubscriptionUsage | null;
  warnings: UsageWarnings | null;
  period: { start: string; end: string } | null;
  invoices: Invoice[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSubscription: () => Promise<void>;
  fetchUsage: () => Promise<void>;
  fetchInvoices: () => Promise<void>;
  createCheckoutSession: (priceId: string, successUrl: string, cancelUrl: string) => Promise<string | null>;
  openBillingPortal: (returnUrl: string) => Promise<string | null>;
  cancelSubscription: () => Promise<void>;
  clearError: () => void;
}


export const useSubscriptionStore = create<SubscriptionState>()((set, get) => ({
  subscription: null,
  limits: null,
  usage: null,
  warnings: null,
  period: null,
  invoices: [],
  isLoading: false,
  error: null,

  fetchSubscription: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<{ subscription: Subscription | null }>('/payments/subscriptions');

      set({
        subscription: data.subscription ?? null,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch subscription',
      });
    }
  },

  fetchUsage: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<{
        tier: string;
        limits: SubscriptionLimits;
        usage: SubscriptionUsage;
        period: { start: string; end: string } | null;
        warnings: UsageWarnings;
      }>('/payments/usage');

      set({
        limits: data.limits ?? null,
        usage: data.usage ?? null,
        warnings: data.warnings ?? null,
        period: data.period ?? null,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch usage',
      });
    }
  },

  fetchInvoices: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<{ invoices: Invoice[] }>('/payments/invoices');

      set({
        invoices: data.invoices ?? [],
        isLoading: false,
        error: null,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch invoices',
      });
    }
  },

  createCheckoutSession: async (
    priceId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<string | null> => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.post<{ sessionId: string; url: string }>(
        '/payments/subscriptions',
        { priceId, successUrl, cancelUrl },
        {
          timeout: 15000, // Longer timeout for payment operations
        }
      );

      set({ isLoading: false, error: null });
      return data.url ?? null;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to create checkout session',
      });
      return null;
    }
  },

  openBillingPortal: async (returnUrl: string): Promise<string | null> => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.post<{ url: string }>(
        '/payments/billing-portal',
        { returnUrl },
        {
          timeout: 15000,
        }
      );

      set({ isLoading: false, error: null });
      return data.url ?? null;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to open billing portal',
      });
      return null;
    }
  },

  cancelSubscription: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      await api.post(
        '/payments/subscriptions/cancel',
        undefined,
        {
          timeout: 15000,
        }
      );

      // Refresh subscription data
      await get().fetchSubscription();
      set({ isLoading: false, error: null });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to cancel subscription',
      });
    }
  },

  clearError: (): void => {
    set({ error: null });
  },
}));
